import {
  type ActivatedRouteSnapshot,
  NavigationEnd,
  type RouterStateSnapshot,
} from '@angular/router';
import { type TranslateService } from '@ngx-translate/core';
import {
  type GlobalHeaderText,
  type GlobalHeaderTextTranslation,
} from '@shared/interfaces/global-header-text';

export type BreadcrumbItem = Readonly<{
  label: string;
  url: string | null;
}>;

type TitleTranslateKey = string | ReadonlyArray<string>;

function isGlobalHeaderTextTranslation(value: unknown): value is GlobalHeaderTextTranslation {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as { lang?: unknown; value?: unknown };
  return typeof candidate.lang === 'string' && typeof candidate.value === 'string';
}

type GlobalHeaderRouteData = Readonly<{
  breadcrumb?: GlobalHeaderText;
  breadcrumbSkip?: boolean;
  titleTranslateKey?: TitleTranslateKey;
  intercomArticleId?: string | number;
  enablePrint?: boolean;
}>;

export type GlobalHeaderStaticModel = Readonly<{
  title: string;
  breadcrumbs: ReadonlyArray<BreadcrumbItem>;
  helpArticleId: string | null;
  printEnabled: boolean;
}>;

export function isNavigationEnd(event: unknown): event is NavigationEnd {
  return event instanceof NavigationEnd;
}

function getPrimaryChildRoute(route: ActivatedRouteSnapshot): ActivatedRouteSnapshot | null {
  return route.children.find(child => child.outlet === 'primary') ?? route.firstChild ?? null;
}

function getDeepestPrimaryRoute(root: ActivatedRouteSnapshot): ActivatedRouteSnapshot {
  let current: ActivatedRouteSnapshot = root;
  while (true) {
    const next = getPrimaryChildRoute(current);
    if (!next) {
      return current;
    }
    current = next;
  }
}

function translateInstantSafe(translateService: TranslateService, key: string): string {
  return String(translateService.instant(key));
}

function getOwnRouteData(route: ActivatedRouteSnapshot): GlobalHeaderRouteData {
  // With `paramsInheritanceStrategy: 'always'`, `route.data` is inherited.
  // For breadcrumbs we need *only this route's* config data + *this route's* resolved keys.
  const staticData = (route.routeConfig?.data as GlobalHeaderRouteData | undefined) ?? {};

  const resolveConfig = route.routeConfig?.resolve;
  if (!resolveConfig) {
    return staticData;
  }

  const resolvedOwnKeys = Object.keys(resolveConfig);
  const resolvedOwnData: Record<string, unknown> = {};
  for (const key of resolvedOwnKeys) {
    const value: unknown = route.data?.[key];
    // `paramsInheritanceStrategy: 'always'` makes `route.data` inherited.
    // If a resolver returns `null`/`undefined` we want the static `data` fallback (if any).
    if (value !== null && value !== undefined) {
      resolvedOwnData[key] = value;
    }
  }

  return {
    ...staticData,
    ...(resolvedOwnData as GlobalHeaderRouteData),
  };
}

function normalizeId(value: string | number | undefined): string | null {
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

function resolveIntercomArticleIdFromRouteData(route: ActivatedRouteSnapshot): string | null {
  let current: ActivatedRouteSnapshot | null = route;

  while (current) {
    const ownData = getOwnRouteData(current);

    const direct = normalizeId(ownData.intercomArticleId);
    if (direct) {
      return direct;
    }

    current = current.parent;
  }

  return null;
}

function resolvePrintEnabledFromRouteData(route: ActivatedRouteSnapshot): boolean {
  return getOwnRouteData(route).enablePrint === true;
}

function resolveLabelFromRouteData(
  route: ActivatedRouteSnapshot,
  translateService: TranslateService
): string | null {
  const ownData = getOwnRouteData(route);

  if (ownData.breadcrumbSkip === true) {
    return null;
  }

  const breadcrumb = ownData.breadcrumb;
  const label = resolveGlobalHeaderText(breadcrumb, translateService);
  if (label) {
    return label;
  }

  const titleTranslateKey = ownData.titleTranslateKey;
  if (typeof titleTranslateKey === 'string' && titleTranslateKey.trim().length > 0) {
    return translateInstantSafe(translateService, titleTranslateKey);
  }

  if (
    Array.isArray(titleTranslateKey) &&
    titleTranslateKey.length > 0 &&
    titleTranslateKey.every(value => typeof value === 'string')
  ) {
    const first = titleTranslateKey.at(0);
    if (typeof first === 'string' && first.trim().length > 0) {
      return translateInstantSafe(translateService, first);
    }
  }

  return null;
}

function resolveTitleFromRouteData(
  route: ActivatedRouteSnapshot,
  translateService: TranslateService
): string | null {
  const ownData = getOwnRouteData(route);

  const titleTranslateKey = ownData.titleTranslateKey;

  if (typeof titleTranslateKey === 'string' && titleTranslateKey.trim().length > 0) {
    return translateInstantSafe(translateService, titleTranslateKey);
  }

  if (
    Array.isArray(titleTranslateKey) &&
    titleTranslateKey.length > 0 &&
    titleTranslateKey.every(value => typeof value === 'string')
  ) {
    const first = titleTranslateKey.at(0);
    if (typeof first === 'string' && first.trim().length > 0) {
      return translateInstantSafe(translateService, first);
    }
  }

  return null;
}

function resolveGlobalHeaderText(
  text: GlobalHeaderText | undefined,
  translateService: TranslateService
) {
  if (typeof text === 'string') {
    const trimmed = text.trim();
    return trimmed.length > 0 ? trimmed : null;
  }

  if (!text || typeof text !== 'object') {
    return null;
  }

  if ('translateKey' in text && typeof text.translateKey === 'string') {
    const trimmed = text.translateKey.trim();
    return trimmed.length > 0 ? translateInstantSafe(translateService, trimmed) : null;
  }

  if ('translations' in text && Array.isArray(text.translations) && text.translations.length > 0) {
    const translations = text.translations.filter(isGlobalHeaderTextTranslation);
    if (translations.length === 0) {
      return null;
    }

    const currentLang = (translateService as unknown as { currentLang?: string }).currentLang;
    const defaultLang = (translateService as unknown as { defaultLang?: string }).defaultLang;
    const preferred = [currentLang, defaultLang].filter(
      (value): value is string => typeof value === 'string' && value.trim().length > 0
    );

    for (const lang of preferred) {
      const match = translations.find(t => t.lang === lang && t.value.trim().length > 0);
      if (match) {
        return match.value;
      }
    }

    const first = translations.find(t => t.value.trim().length > 0);
    return first?.value ?? null;
  }

  return null;
}

function buildBreadcrumbs(
  routerState: RouterStateSnapshot,
  translateService: TranslateService
): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = [];

  let route: ActivatedRouteSnapshot | null = routerState.root;
  let url = '';

  while (route) {
    const child = getPrimaryChildRoute(route);
    if (!child) {
      break;
    }

    route = child;

    const segment = route.url
      .map(s => s.path)
      .filter(Boolean)
      .join('/');
    if (segment) {
      url += `/${segment}`;
    }

    const label = resolveLabelFromRouteData(route, translateService);
    if (label) {
      const item: BreadcrumbItem = { label, url: url || '/' };
      const previous = breadcrumbs.at(-1);
      if (!previous || previous.label !== item.label || previous.url !== item.url) {
        breadcrumbs.push(item);
      }
    }
  }

  return breadcrumbs;
}

export function buildGlobalHeaderStaticModel(
  routerStateSnapshot: RouterStateSnapshot,
  translateService: TranslateService
): GlobalHeaderStaticModel {
  const deepestRoute = getDeepestPrimaryRoute(routerStateSnapshot.root);

  const breadcrumbs = buildBreadcrumbs(routerStateSnapshot, translateService);
  const title =
    breadcrumbs.at(-1)?.label ?? resolveTitleFromRouteData(deepestRoute, translateService) ?? '';

  return {
    title,
    breadcrumbs,
    helpArticleId: resolveIntercomArticleIdFromRouteData(deepestRoute),
    printEnabled: resolvePrintEnabledFromRouteData(deepestRoute),
  };
}
